import { useApolloClient } from '@apollo/client';
import { Query } from '@apollo/client/react/components';
import gql from 'graphql-tag';
import { once, isEmpty } from 'lodash';
import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { HISTORY, SUBSCRIBE_EVENTS, SUBSCRIBE_MESSAGES } from '../../actions/Messages';

const YODA_ENTRYPOINT_ATTRIBUTE_KEY = 'yoda_entrypoint';
const DEFAULT_YODA_ENTRYPOINT = 'NOT_PROVIDED_BY_IFRAME';

export const GET_USER = gql`
  query getChatUser(
    $channelId: ID!
    $chatUserId: ID
    $email: String
    $firstname: String
    $lastname: String
    $language: String
    $mobile: String
    $phone: String
    $attributes: [InputChatUserAttribute!]
    $userAttributesToken: String
  ) {
    user: getChatUser(
      chatUserId: $chatUserId
      channelId: $channelId
      email: $email
      firstname: $firstname
      lastname: $lastname
      language: $language
      mobile: $mobile
      phone: $phone
      attributes: $attributes
      userAttributesToken: $userAttributesToken
    ) {
      id
    }
  }
`;

const INIT_CHAT = gql`
  query initChatUserChat($chatUserId: ID!, $channelId: ID!, $skills: [ID!], $contextMessage: String) {
    initChatUserChat(chatUserId: $chatUserId, channelId: $channelId, skills: $skills, contextMessage: $contextMessage)
  }
`;

const CHECK_USER = gql`
  query doesChatUserExist($chatUserId: ID!, $channelId: ID!, $persistentSlug: String) {
    exist: doesChatUserExist(chatUserId: $chatUserId, channelId: $channelId, persistentSlug: $persistentSlug)
  }
`;

const CheckUser = ({
  data = { user: {} },
  slug,
  client,
  subscribeToMore,
  channelId,
  persistor,
  session,
  children,
  attributes,
  skills,
  contextMessage,
  chatOpen,
}) => {
  console.log('CheckUser - received channelId:', channelId);

  const messagesRef = useRef([]);
  const [isTyping, setIsTyping] = useState(false);
  const typingTimeoutRef = useRef(null);
  const subscriptionsSetupRef = useRef(false);
  const chatUserId = data.user.id || localStorage.getItem(`chatUserId_${channelId}`);

  const subscribe = useMemo(
    () => () => {
      if (!chatUserId) return;

      // Only set up subscriptions once, even if component re-renders
      if (subscriptionsSetupRef.current) {
        console.log('Subscriptions already set up, skipping...');
        return;
      }

      console.log('Setting up subscriptions for chatUserId:', chatUserId);
      subscriptionsSetupRef.current = true;

      subscribeToMore({
        document: SUBSCRIBE_MESSAGES,
        variables: { chatUserId },
        updateQuery: (_, { subscriptionData: { data = {} } = {} }) => {
          if (
            data &&
            data.messageAdded &&
            ['channels', 'bot', 'users'].includes(data.messageAdded.authorType) &&
            (document.visibilityState === 'hidden' ||
              localStorage.getItem('window_focus') === 'false' ||
              localStorage.getItem('webchat_open') === 'false')
          ) {
            window.parent.postMessage(JSON.stringify({ action: 'new_messages' }), '*');
          }

          if (data.messageAdded) {
            // Clear any existing timeout
            if (typingTimeoutRef.current) {
              clearTimeout(typingTimeoutRef.current);
              typingTimeoutRef.current = null;
            }

            // Show typing indicator when user sends message in bot channel (no agent assigned)
            if (data.messageAdded.authorType === 'chatUsers' && !data.messageAdded.isAgentAssigned) {
              setIsTyping(true);
              // Auto-hide typing indicator after 30 seconds if no bot response
              typingTimeoutRef.current = setTimeout(() => {
                setIsTyping(false);
                typingTimeoutRef.current = null;
              }, 30000);
            }
            // Hide typing indicator when bot/agent responds
            else if (['channels', 'bot', 'users'].includes(data.messageAdded.authorType)) {
              setIsTyping(false);
            }
          }

          let historyResult;
          try {
            historyResult = client.readQuery({
              query: HISTORY,
              variables: { channelId, chatUserId },
            });
          } catch (e) {
            // Error handling (optional)
          }

          if (historyResult && historyResult.history) {
            const prevMessages = messagesRef.current.slice();
            messagesRef.current = [];
            try {
              const newHistory = [
                ...(historyResult.history || []),
                ...prevMessages.filter(message => !historyResult.history.some(hMessage => hMessage.id === message.id)),
                data.messageAdded,
              ];
              client.writeQuery({
                query: HISTORY,
                variables: { channelId, chatUserId },
                data: { history: newHistory },
              });
            } catch (e) {
              // Error handling (optional)
            }
          } else {
            messagesRef.current = messagesRef.current.concat(data.messageAdded);
          }
        },
      });
      subscribeToMore({
        document: SUBSCRIBE_EVENTS,
        variables: { chatUserId },
        updateQuery: (_, { subscriptionData: { data: { eventAdded = {} } = { eventAdded: {} } } = {} }) => {
          // Handle typing events from server
          if (eventAdded.isTyping !== undefined) {
            if (eventAdded.isTyping) {
              // Clear any existing timeout when server indicates typing
              if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
                typingTimeoutRef.current = null;
              }
              setIsTyping(true);
            } else {
              // Hide typing indicator when server indicates not typing
              if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
                typingTimeoutRef.current = null;
              }
              setIsTyping(false);
            }
          }
        },
      });
    },
    [chatUserId]
  );
  useEffect(subscribe, [subscribe]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Query
      query={INIT_CHAT}
      fetchPolicy="network-only"
      variables={{ chatUserId: chatUserId, channelId: channelId, skills, contextMessage }}
    >
      {({ loading }) => {
        console.log('INIT_CHAT query - variables:', { chatUserId, channelId, skills, contextMessage });
        if (loading) return null;
        return (
          <Query
            fetchPolicy="network-only"
            query={CHECK_USER}
            variables={{
              chatUserId,
              channelId: channelId,
              persistentSlug: slug,
            }}
          >
            {({ data: exist, loading, error }) => {
              console.log('CHECK_USER query - variables:', { chatUserId, channelId, persistentSlug: slug });
              console.log('CHECK_USER query - result:', { exist, loading, error });

              if (loading) return null;
              if (error || (exist && !exist.exist)) {
                console.error('CHECK_USER error or user does not exist:', error, exist);
                persistor.pause();
                persistor.purge().then(() => window.location.reload());
                return <span>ChatUser in cache does not exist, clear localstorage</span>;
              }
              if (data.user.id) {
                localStorage.setItem(`chatUserId_${channelId}`, data.user.id);
              }
              return (
                <Fragment>
                  {chatOpen &&
                    children({ children, user: data.user, isTyping, messagesFromSubscription: messagesRef.current })}
                </Fragment>
              );
            }}
          </Query>
        );
      }}
    </Query>
  );
};

const emitUserId = once(id => {
  window.parent.postMessage(JSON.stringify({ action: 'user-created', id }), '*');
});

const resolveYodaEntrypoint = yodaEntrypoint => {
  if (typeof yodaEntrypoint !== 'string') return DEFAULT_YODA_ENTRYPOINT;
  const trimmed = yodaEntrypoint.trim();
  if (!trimmed) return DEFAULT_YODA_ENTRYPOINT;
  return trimmed;
};

export default ({
  slug,
  channelId,
  persistor,
  children,
  chatUserId: propsChatUserId,
  session = {},
  saveHistory = true,
  subscribeToMore,
  chatOpen = true,
}) => {
  console.log('ChatUserReceiver - received channelId:', channelId);
  console.log('ChatUserReceiver - session:', session);

  const apolloClient = useApolloClient();
  const browserLocale =
    session.locale || (navigator.languages !== undefined ? navigator.languages[0] : navigator.language || 'DEFAULT');
  const browersLocaleShort = browserLocale.substr(0, 2).toUpperCase();

  console.log('ChatUserReceiver - browserLocale:', browserLocale);
  console.log('ChatUserReceiver - browersLocaleShort:', browersLocaleShort);

  const attributes = useMemo(() => {
    const {
      firstname,
      lastname,
      locale,
      email,
      mobile,
      telephone,
      skills,
      context,
      authToken,
      userAttributesToken,
      yoda_entrypoint,
      ...remainingSession
    } = session || {};
    const attributesFromSession = !!remainingSession && !isEmpty(remainingSession)
      ? Object.entries(remainingSession).reduce((arr, [key, value]) => {
          return arr.concat({ key, value });
        }, [])
      : [];

    return attributesFromSession.concat({
      key: YODA_ENTRYPOINT_ATTRIBUTE_KEY,
      value: resolveYodaEntrypoint(yoda_entrypoint),
    });
  }, [session]);

  const chatUserId = propsChatUserId || localStorage.getItem(`chatUserId_${channelId}`);

  console.log('Storage chatUser::', chatUserId);

  return (
    <Query
      query={GET_USER}
      fetchPolicy="network-only"
      variables={{
        channelId,
        chatUserId: chatUserId,
        email: session.email,
        firstname: session.firstname,
        lastname: session.lastname,
        language: browersLocaleShort || null,
        mobile: session.mobile,
        phone: session.telephone,
        attributes,
        userAttributesToken: session.userAttributesToken,
      }}
    >
      {getUserProps => {
        console.log('GET_USER query - variables channelId:', channelId);
        console.log('GET_USER query - getUserProps:', getUserProps);

        if (getUserProps.loading) return null;
        if (getUserProps.error) {
          console.error('GET_USER error:', getUserProps.error);
          return <div>Error fetching user</div>;
        }
        const newChatUserId = getUserProps.data && getUserProps.data.user && getUserProps.data.user.id;

        console.log('is new chat user::', newChatUserId !== chatUserId, newChatUserId, chatUserId);

        emitUserId(newChatUserId);

        return (
          <CheckUser
            {...getUserProps}
            client={apolloClient}
            loading={false}
            channelId={channelId}
            persistor={persistor}
            session={session}
            children={children}
            subscribeToMore={subscribeToMore}
            slug={slug}
            attributes={attributes}
            skills={session.skills}
            contextMessage={session.context}
            chatOpen={chatOpen}
          />
        );
      }}
    </Query>
  );
};
